import React, { useRef, useState, useEffect } from 'react';
import '../../styles/EvidenceModal.css';
import { useMediaCapture } from '../../hooks/useMediaCapture';
import { useCameraControls } from '../../hooks/useCameraControls';
import { useDeviceDetection } from '../../hooks/useDeviceDetection';

// Función helper para determinar la extensión correcta basada en el tipo MIME
const getFileExtension = (mimeType) => {
  if (mimeType.startsWith('video/')) {
    // Siempre usar .mp4 para videos, el backend se encargará de la conversión
    return '.mp4';
  } else if (mimeType.startsWith('audio/')) {
    // Siempre usar .mp3 para audio, el backend se encargará de la conversión
    return '.mp3';
  } else if (mimeType.startsWith('image/')) {
    if (mimeType.includes('jpeg') || mimeType.includes('jpg')) {
      return '.jpg';
    } else if (mimeType.includes('png')) {
      return '.png';
    } else if (mimeType.includes('gif')) {
      return '.gif';
    } else if (mimeType.includes('webp')) {
      return '.webp';
    }
    return '.jpg'; // Por defecto JPG
  }
  return '.mp4'; // Fallback
};

const EvidenceModal = ({ show, handleClose, title, type, onFileSelect, locationData }) => {
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const audioInputRef = useRef(null);
  const webcamRef = useRef(null);
  
  const [captureMode, setCaptureMode] = useState('preview');
  const [showPreview, setShowPreview] = useState(false);
  const [error, setError] = useState(null);
  
  const deviceInfo = useDeviceDetection();
  const mediaCapture = useMediaCapture();
  const cameraControls = useCameraControls();

  useEffect(() => {
    console.log('🔄 useEffect - Modal state changed:', { show, type, captureMode });
    
    if (show && (type === 'photo' || type === 'video') && captureMode === 'preview') {
      console.log('📹 Iniciando cámara para:', type);
      cameraControls.startCamera(type);
    }
    if (show && type === 'audio' && captureMode === 'preview') {
      console.log('🎤 Iniciando audio');
      cameraControls.startCamera('audio');
    }
    return () => {
      if (!show) {
        console.log('🛑 Limpiando modal');
        cameraControls.resetCamera();
        mediaCapture.resetCapture();
        setShowPreview(false);
      }
    };
  }, [show, type, captureMode]);

  // Efecto para reiniciar cámara cuando hay errores
  useEffect(() => {
    if (cameraControls.error && show) {
      console.log('🔄 Error detectado, reiniciando cámara...');
      setTimeout(() => {
        cameraControls.clearStates(); // Limpiar estados primero
        cameraControls.resetCamera();
      }, 2000);
    }
  }, [cameraControls.error, show]);

  // Efecto para limpiar estados si se quedan colgados
  useEffect(() => {
    if (show && (cameraControls.isStartingCamera || cameraControls.isSwitchingCamera)) {
      const timeout = setTimeout(() => {
        if (cameraControls.isStartingCamera || cameraControls.isSwitchingCamera) {
          console.log('⚠️ Estados colgados detectados, limpiando...');
          cameraControls.clearStates();
        }
      }, 10000); // 10 segundos de timeout

      return () => clearTimeout(timeout);
    }
  }, [show, cameraControls.isStartingCamera, cameraControls.isSwitchingCamera]);

  // Efecto para limpiar estado cuando se cierra el modal
  useEffect(() => {
    if (!show) {
      // Limpiar estado cuando se cierra el modal
      setTimeout(() => {
        cameraControls.resetCamera();
        mediaCapture.resetCapture();
        setShowPreview(false);
        setError(null);
      }, 100);
    }
  }, [show]);

  // Vincular stream a elemento <video>
  useEffect(() => {
    if (webcamRef.current && cameraControls.stream && (type === 'photo' || type === 'video') && captureMode === 'preview') {
      try {
        if (webcamRef.current.srcObject !== cameraControls.stream) {
          webcamRef.current.srcObject = cameraControls.stream;
        }
      } catch (e) {
        console.error('No se pudo asignar srcObject al video:', e);
      }
    }
  }, [cameraControls.stream, type, captureMode]);

  // Efecto para ocultar elementos de navegación en fullscreen
  useEffect(() => {
    if (show && (type === 'photo' || type === 'video') && captureMode === 'preview') {
      document.body.classList.add('camera-fullscreen-active');
    } else {
      document.body.classList.remove('camera-fullscreen-active');
    }

    return () => {
      document.body.classList.remove('camera-fullscreen-active');
    };
  }, [show, type, captureMode]);

  useEffect(() => {
    if (mediaCapture.mediaBlob && !mediaCapture.recording) {
      setShowPreview(true);
    }
  }, [mediaCapture.mediaBlob, mediaCapture.recording]);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (mediaCapture.handleFileSelect(file, type)) {
        onFileSelect(file);
        handleClose();
      } else {
        setError('Tipo de archivo no válido. El tamaño será validado por el servidor.');
      }
    }
  };

  const triggerFileSelect = () => fileInputRef.current?.click();

  const renderContent = () => {
    switch (type) {
      case 'photo':
        return (
          <div className="modal-content">
            <div className="mode-toggle">
              <button 
                onClick={() => { 
                  setCaptureMode('preview'); 
                  setShowPreview(false); 
                  mediaCapture.resetCapture(); 
                }} 
                className={`mode-btn ${captureMode === 'preview' ? 'active' : ''}`}
              >
                <i className="fas fa-camera"></i>
                Vista Previa
              </button>
              {deviceInfo.nativeCameraSupported && (
                <button 
                  onClick={() => { 
                    setCaptureMode('native'); 
                    cameraControls.stopCamera(); 
                    setShowPreview(false); 
                    mediaCapture.resetCapture(); 
                  }} 
                  className={`mode-btn ${captureMode === 'native' ? 'active' : ''}`}
                >
                  <i className="fas fa-mobile-alt"></i>
                  Cámara Nativa
                </button>
              )}
              <button 
                onClick={() => { 
                  setCaptureMode('upload'); 
                  cameraControls.stopCamera(); 
                  setShowPreview(false); 
                  mediaCapture.resetCapture(); 
                }} 
                className={`mode-btn ${captureMode === 'upload' ? 'active' : ''}`}
              >
                <i className="fas fa-images"></i>
                Galería
              </button>
            </div>

            {captureMode === 'preview' && (
              <div className="camera-preview fullscreen">
                {cameraControls.stream && !showPreview ? (
                  <div className="webcam-container">
                    <video ref={webcamRef} autoPlay playsInline muted className="webcam-video" />
                    <button 
                      className="exit-fullscreen-btn"
                      onClick={handleClose}
                      title="Salir de pantalla completa"
                    >
                      <i className="fas fa-times"></i>
                    </button>
                    <div className="camera-overlay">
                      <div className="camera-controls">
                        
                                                 <button 
                                                       onClick={async () => {
                              console.log('📸 Botón de captura clickeado');
                              
                              // Mostrar indicador de obtención de ubicación
                              setError('Obteniendo ubicación...');
                              
                              // Solicitar ubicación antes de capturar
                              if (navigator.geolocation) {
                                try {
                                  const position = await new Promise((resolve, reject) => {
                                    navigator.geolocation.getCurrentPosition(resolve, reject, {
                                      enableHighAccuracy: true,
                                      timeout: 10000,
                                      maximumAge: 60000
                                    });
                                  });
                                  
                                  console.log('📍 Ubicación obtenida:', position.coords);
                                  
                                  // Obtener dirección usando las coordenadas
                                  let address = 'Ubicación no disponible';
                                  try {
                                    // Usar una API gratuita sin key
                                    const fallbackUrl = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${position.coords.latitude}&lon=${position.coords.longitude}&zoom=18&addressdetails=1&accept-language=es`;
                                    
                                    // Timeout de 3 segundos para no demorar la captura
                                    const controller = new AbortController();
                                    const timeoutId = setTimeout(() => controller.abort(), 3000);
                                    
                                    const response = await fetch(fallbackUrl, { signal: controller.signal });
                                    clearTimeout(timeoutId);
                                    const geocodingData = await response.json();
                                    
                                    if (geocodingData.display_name) {
                                      address = geocodingData.display_name;
                                      console.log('📍 Dirección obtenida:', address);
                                    }
                                  } catch (geocodingError) {
                                    console.warn('⚠️ No se pudo obtener dirección:', geocodingError);
                                  }
                                  
                                  // Actualizar locationData con coordenadas reales y dirección
                                  const updatedLocationData = {
                                    ...locationData,
                                    latitude: position.coords.latitude,
                                    longitude: position.coords.longitude,
                                    address: address
                                  };
                                  
                                  if (webcamRef.current && webcamRef.current.readyState >= 2) {
                                    console.log('📹 Video listo, capturando...');
                                    setError(null); // Limpiar mensaje de ubicación
                                    try {
                                      await mediaCapture.capturePhoto(webcamRef, 'photo', setError, updatedLocationData);
                                      setShowPreview(true);
                                    } catch (error) {
                                      console.error('❌ Error capturando foto:', error);
                                      setError('Error al capturar la foto');
                                    }
                                  } else {
                                    console.log('❌ Video no está listo');
                                    setError('Espera a que la cámara esté lista');
                                  }
                                                                  } catch (geoError) {
                                    console.warn('⚠️ No se pudo obtener ubicación:', geoError);
                                    // Continuar sin ubicación
                                    if (webcamRef.current && webcamRef.current.readyState >= 2) {
                                      console.log('📹 Video listo, capturando sin ubicación...');
                                      setError(null); // Limpiar mensaje de ubicación
                                      try {
                                        await mediaCapture.capturePhoto(webcamRef, 'photo', setError, locationData);
                                        setShowPreview(true);
                                      } catch (error) {
                                        console.error('❌ Error capturando foto:', error);
                                        setError('Error al capturar la foto');
                                      }
                                    } else {
                                      console.log('❌ Video no está listo');
                                      setError('Espera a que la cámara esté lista');
                                    }
                                  }
                              } else {
                                console.warn('⚠️ Geolocalización no disponible');
                                // Continuar sin ubicación
                                if (webcamRef.current && webcamRef.current.readyState >= 2) {
                                  console.log('📹 Video listo, capturando sin ubicación...');
                                  setError(null); // Limpiar mensaje de ubicación
                                  try {
                                    await mediaCapture.capturePhoto(webcamRef, 'photo', setError, locationData);
                                    setShowPreview(true);
                                  } catch (error) {
                                    console.error('❌ Error capturando foto:', error);
                                    setError('Error al capturar la foto');
                                  }
                                } else {
                                  console.log('❌ Video no está listo');
                                  setError('Espera a que la cámara esté lista');
                                }
                              }
                            }} 
                           disabled={cameraControls.isStartingCamera || mediaCapture.processingPhoto}
                           className="control-btn capture-btn"
                         >
                           <i className={`fas ${mediaCapture.processingPhoto ? 'fa-spinner fa-spin' : 'fa-camera'}`} title="Capturar"></i>
                         </button>
                      </div>
                    </div>
                  </div>
                ) : showPreview && mediaCapture.mediaBlobUrl ? (
                                     <div className="preview-container fullscreen">
                     <img src={mediaCapture.mediaBlobUrl} alt="Preview" className="preview-image" />
                     <button 
                       className="exit-fullscreen-btn"
                       onClick={handleClose}
                       title="Salir de pantalla completa"
                     >
                       <i className="fas fa-times"></i>
                     </button>
                     <div className="preview-overlay">
                       <div className="preview-controls">
                         <button 
                           onClick={() => { 
                             setShowPreview(false); 
                             mediaCapture.resetCapture(); 
                           }} 
                           className="control-btn retake-btn"
                         >
                           <i className="fas fa-redo" title="Repetir"></i>
                         </button>
                         <button 
                           onClick={() => { 
                             if (mediaCapture.mediaBlob) { 
                               // Convertir Blob a File con nombre y tipo apropiado para foto
                               const extension = getFileExtension(mediaCapture.mediaBlob.type);
                               const photoFile = new File([mediaCapture.mediaBlob], `foto_${Date.now()}${extension}`, { 
                                 type: mediaCapture.mediaBlob.type 
                               });
                               onFileSelect(photoFile); 
                               cameraControls.resetCamera();
                               handleClose(); 
                             } 
                           }} 
                           className="control-btn confirm-btn"
                         >
                           <i className="fas fa-check" title="Confirmar"></i>
                         </button>
                       </div>
                     </div>
                   </div>
                ) : (
                  <div className="camera-loading fullscreen">
                    <div className="loading-spinner"></div>
                    <p>Iniciando cámara...</p>
                  </div>
                )}
              </div>
            )}

            {captureMode === 'native' && (
              <div className="native-camera">
                <button 
                  onClick={() => cameraInputRef.current?.click()} 
                  className="upload-btn camera-btn"
                >
                  <i className="fas fa-camera"></i>
                  Abrir Cámara Nativa
                </button>
                <input 
                  ref={cameraInputRef} 
                  type="file" 
                  accept="image/*" 
                  capture="environment" 
                  onChange={handleFileSelect} 
                  style={{ display: 'none' }} 
                />
              </div>
            )}

            {captureMode === 'upload' && (
              <div className="upload-options">
                <button 
                  onClick={triggerFileSelect} 
                  className="upload-btn gallery-btn"
                >
                  <i className="fas fa-images"></i>
                  Seleccionar desde Galería
                </button>
                <input 
                  ref={fileInputRef} 
                  type="file" 
                  accept="image/*" 
                  onChange={handleFileSelect} 
                  style={{ display: 'none' }} 
                />
              </div>
            )}

            {error && (
              <div className="error-message">
                <i className="fas fa-exclamation-triangle"></i>
                {error}
              </div>
            )}
          </div>
        );

      case 'video':
        return (
          <div className="modal-content">
            <div className="mode-toggle">
              <button 
                onClick={() => { 
                  setCaptureMode('preview'); 
                  setShowPreview(false); 
                  mediaCapture.resetCapture(); 
                }} 
                className={`mode-btn ${captureMode === 'preview' ? 'active' : ''}`}
              >
                <i className="fas fa-video"></i>
                Vista Previa
              </button>
              {deviceInfo.nativeCameraSupported && (
                <button 
                  onClick={() => { 
                    setCaptureMode('native'); 
                    cameraControls.stopCamera(); 
                    setShowPreview(false); 
                    mediaCapture.resetCapture(); 
                  }} 
                  className={`mode-btn ${captureMode === 'native' ? 'active' : ''}`}
                >
                  <i className="fas fa-mobile-alt"></i>
                  Cámara Nativa
                </button>
              )}
              <button 
                onClick={() => { 
                  setCaptureMode('upload'); 
                  cameraControls.stopCamera(); 
                  setShowPreview(false); 
                  mediaCapture.resetCapture(); 
                }} 
                className={`mode-btn ${captureMode === 'upload' ? 'active' : ''}`}
              >
                <i className="fas fa-film"></i>
                Galería
              </button>
            </div>

            {captureMode === 'preview' && (
              <div className="camera-preview fullscreen">
                                 {cameraControls.stream && !showPreview ? (
                   <div className="webcam-container">
                     <video ref={webcamRef} autoPlay playsInline muted className="webcam-video" />
                     <button 
                       className="exit-fullscreen-btn"
                       onClick={handleClose}
                       title="Salir de pantalla completa"
                     >
                       <i className="fas fa-times"></i>
                     </button>
                     
                     {/* Cronómetro de grabación */}
                     {mediaCapture.recording && (
                       <div className="recording-timer-overlay">
                         <div className="recording-indicator">
                           <span className="recording-dot"></span>
                           {mediaCapture.formatRecordingTime()}
                         </div>
                       </div>
                     )}
                     
                     <div className="camera-overlay">
                       <div className="camera-controls">
                                                   <button 
                            onClick={async () => {
                              console.log('🔄 Botón de cambio de cámara (video) clickeado');
                              
                              // Si hay estados colgados, limpiarlos primero
                              if (cameraControls.isStartingCamera || cameraControls.isSwitchingCamera) {
                                console.log('🧹 Limpiando estados colgados antes de cambiar cámara...');
                                cameraControls.clearStates();
                                await new Promise(resolve => setTimeout(resolve, 500));
                              }
                              
                              try {
                                await cameraControls.resetAndSwitchCamera('video');
                              } catch (error) {
                                console.error('❌ Error cambiando cámara:', error);
                                cameraControls.clearStates();
                              }
                            }} 
                            disabled={cameraControls.isSwitchingCamera || cameraControls.isStartingCamera} 
                            className="control-btn switch-btn"
                          >
                            <i className={`fas ${cameraControls.isSwitchingCamera ? 'fa-spinner fa-spin' : 'fa-sync-alt'}`} title="Cambiar cámara"></i>
                          </button>
                                                                             {!mediaCapture.recording ? (
                             <button 
                               onClick={async () => {
                                 console.log('🎬 Botón de grabación clickeado');
                                 if (cameraControls.stream) {
                                   try {
                                     await mediaCapture.startRecording(cameraControls.stream, 'video', setError);
                                   } catch (error) {
                                     console.error('❌ Error iniciando grabación:', error);
                                     setError('Error al iniciar la grabación');
                                   }
                                 } else {
                                   setError('Cámara no disponible para grabación');
                                 }
                               }} 
                               disabled={cameraControls.isStartingCamera} 
                               className="control-btn capture-btn"
                             >
                               <i className="fas fa-video" title="Grabar"></i>
                             </button>
                           ) : (
                            <button 
                              onClick={() => {
                                console.log('🛑 Botón de detener clickeado');
                                mediaCapture.stopRecording();
                              }} 
                              className="control-btn retake-btn"
                            >
                              <i className="fas fa-stop" title="Detener"></i>
                            </button>
                          )}
                       </div>
                     </div>
                   </div>
                                 ) : showPreview && mediaCapture.mediaBlobUrl ? (
                   <div className="preview-container fullscreen">
                     <video src={mediaCapture.mediaBlobUrl} controls className="preview-image" />
                     <button 
                       className="exit-fullscreen-btn"
                       onClick={handleClose}
                       title="Salir de pantalla completa"
                     >
                       <i className="fas fa-times"></i>
                     </button>
                     <div className="preview-overlay">
                       <div className="preview-controls">
                         <button 
                           onClick={() => { 
                             setShowPreview(false); 
                             mediaCapture.resetCapture(); 
                           }} 
                           className="control-btn retake-btn"
                         >
                           <i className="fas fa-redo" title="Repetir"></i>
                         </button>
                         <button 
                           onClick={() => { 
                             if (mediaCapture.mediaBlob) { 
                               // Convertir Blob a File con nombre y tipo apropiado para video
                               const extension = getFileExtension(mediaCapture.mediaBlob.type);
                               const videoFile = new File([mediaCapture.mediaBlob], `video_${Date.now()}${extension}`, { 
                                 type: mediaCapture.mediaBlob.type 
                               });
                               onFileSelect(videoFile); 
                               cameraControls.resetCamera();
                               handleClose(); 
                             } 
                           }} 
                           className="control-btn confirm-btn"
                         >
                           <i className="fas fa-check" title="Confirmar"></i>
                         </button>
                       </div>
                     </div>
                   </div>
                ) : (
                  <div className="camera-loading fullscreen">
                    <div className="loading-spinner"></div>
                    <p>Iniciando cámara...</p>
                  </div>
                )}
              </div>
            )}

            {captureMode === 'native' && (
              <div className="native-camera">
                <button 
                  onClick={() => videoInputRef.current?.click()} 
                  className="upload-btn camera-btn"
                >
                  <i className="fas fa-video"></i>
                  Abrir Cámara Nativa
                </button>
                <input 
                  ref={videoInputRef} 
                  type="file" 
                  accept="video/*" 
                  capture="camcorder" 
                  onChange={handleFileSelect} 
                  style={{ display: 'none' }} 
                />
              </div>
            )}

            {captureMode === 'upload' && (
              <div className="upload-options">
                <button 
                  onClick={triggerFileSelect} 
                  className="upload-btn gallery-btn"
                >
                  <i className="fas fa-film"></i>
                  Seleccionar desde Galería
                </button>
                <input 
                  ref={fileInputRef} 
                  type="file" 
                  accept="video/*" 
                  onChange={handleFileSelect} 
                  style={{ display: 'none' }} 
                />
              </div>
            )}

            {error && (
              <div className="error-message">
                <i className="fas fa-exclamation-triangle"></i>
                {error}
              </div>
            )}
          </div>
        );

      case 'audio':
        return (
          <div className="modal-content">
            <div className="action-grid">
              <button 
                onClick={() => { setCaptureMode('preview'); setShowPreview(false); mediaCapture.resetCapture(); }} 
                className={`action-card primary ${captureMode === 'preview' ? 'active' : ''}`}
              >
                <i className="fas fa-microphone"></i>
                Grabar Audio
              </button>
              <button 
                onClick={() => { setCaptureMode('upload'); cameraControls.stopCamera(); setShowPreview(false); mediaCapture.resetCapture(); }} 
                className={`action-card secondary ${captureMode === 'upload' ? 'active' : ''}`}
              >
                <i className="fas fa-music"></i>
                Subir Audio
              </button>
            </div>

            {captureMode === 'preview' && (
              <div className="audio-container">
                {!showPreview ? (
                  <div className="audio-recording-area">
                    <div className="audio-icon">🎤</div>
                    <div className="recording-timer">
                      {mediaCapture.recording ? mediaCapture.formatRecordingTime() : '00:00'}
                    </div>
                    <div className="audio-visualizer">
                      <div className="visualizer-bar"></div>
                      <div className="visualizer-bar"></div>
                      <div className="visualizer-bar"></div>
                      <div className="visualizer-bar"></div>
                      <div className="visualizer-bar"></div>
                    </div>
                  </div>
                ) : mediaCapture.mediaBlobUrl ? (
                  <div className="audio-preview-area">
                    <audio src={mediaCapture.mediaBlobUrl} controls style={{ width: '100%', maxWidth: '400px' }} />
                  </div>
                ) : null}
                
                {/* Modal Footer para Audio */}
                <div className="audio-modal-footer">
                  {!mediaCapture.recording && !showPreview ? (
                    <button 
                      onClick={async () => {
                        try {
                          await mediaCapture.startRecording(cameraControls.stream, 'audio', setError);
                        } catch (error) {
                          console.error('❌ Error iniciando grabación de audio:', error);
                          setError('Error al iniciar la grabación de audio');
                        }
                      }} 
                      className="control-btn capture-btn"
                    >
                      <i className="fas fa-circle" title="Iniciar Grabación"></i>
                    </button>
                  ) : mediaCapture.recording ? (
                    <button 
                      onClick={() => mediaCapture.stopRecording()} 
                      className="control-btn retake-btn"
                    >
                      <i className="fas fa-stop" title="Detener Grabación"></i>
                    </button>
                  ) : showPreview && mediaCapture.mediaBlob ? (
                    <>
                      <button 
                        onClick={() => { 
                          setShowPreview(false); 
                          mediaCapture.resetCapture(); 
                        }} 
                        className="control-btn retake-btn"
                      >
                        <i className="fas fa-redo" title="Repetir"></i>
                      </button>
                      <button 
                        onClick={() => { 
                          const extension = getFileExtension(mediaCapture.mediaBlob.type);
                          const audioFile = new File([mediaCapture.mediaBlob], `audio_${Date.now()}${extension}`, { 
                            type: mediaCapture.mediaBlob.type 
                          });
                          onFileSelect(audioFile); 
                          handleClose(); 
                        }} 
                        className="control-btn confirm-btn"
                      >
                        <i className="fas fa-check" title="Confirmar"></i>
                      </button>
                    </>
                  ) : null}
                </div>
              </div>
            )}

            {captureMode === 'native' && (
              <div className="native-audio">
                <button 
                  onClick={() => audioInputRef.current?.click()} 
                  className="upload-btn audio-btn"
                >
                  <i className="fas fa-microphone"></i>
                  Abrir Micrófono Nativo
                </button>
                <input 
                  ref={audioInputRef} 
                  type="file" 
                  accept="audio/*" 
                  capture="microphone" 
                  onChange={handleFileSelect} 
                  style={{ display: 'none' }} 
                />
              </div>
            )}

            {captureMode === 'upload' && (
              <div className="upload-options">
                <button 
                  onClick={triggerFileSelect} 
                  className="upload-btn gallery-btn"
                >
                  <i className="fas fa-music"></i>
                  Seleccionar desde Galería
                </button>
                <input 
                  ref={fileInputRef} 
                  type="file" 
                  accept="audio/*" 
                  onChange={handleFileSelect} 
                  style={{ display: 'none' }} 
                />
              </div>
            )}

            {error && (
              <div className="error-message">
                <i className="fas fa-exclamation-triangle"></i>
                {error}
              </div>
            )}
          </div>
        );

      case 'file':
        return (
          <div className="modal-content">
            <div className="upload-options">
              <button 
                onClick={triggerFileSelect} 
                className="upload-btn gallery-btn"
              >
                <i className="fas fa-file-upload"></i>
                Seleccionar Documento
              </button>
              <input 
                ref={fileInputRef} 
                type="file" 
                accept=".pdf,.doc,.docx,.txt,.rtf,.odt,.pages,.xls,.xlsx,.csv,.ods,.numbers,.ppt,.pptx,.key,.odp" 
                onChange={handleFileSelect} 
                style={{ display: 'none' }} 
              />
              <div className="upload-info">
                <small style={{ color: '#666', fontStyle: 'italic', marginTop: '10px', display: 'block', textAlign: 'center' }}>
                  <i className="fas fa-info-circle"></i>
                  Formatos soportados: PDF, Word, Excel, PowerPoint, Texto. El tamaño será validado por el servidor.
                </small>
              </div>
            </div>

            {error && (
              <div className="error-message">
                <i className="fas fa-exclamation-triangle"></i>
                {error}
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  if (!show) return null;

  return (
    <div className="evidence-modal-overlay" onClick={handleClose}>
      {cameraControls.isSwitchingCamera && (
        <div className="status-message">
          <i className="fas fa-spinner fa-spin"></i>
          Cambiando cámara...
        </div>
      )}
      <div className="evidence-modal" onClick={(e) => e.stopPropagation()}>
        <div className="evidence-modal-header">
          <h3 className="evidence-modal-title">{title}</h3>
          <div className="evidence-modal-actions">
            <button 
              className="evidence-modal-back-btn" 
              onClick={() => {
                cameraControls.resetCamera();
                mediaCapture.resetCapture();
                setShowPreview(false);
                handleClose();
              }}
              title="Salir y regresar al formulario"
            >
              <i className="fas fa-arrow-left"></i>
              Salir
            </button>
            <button className="evidence-modal-close" onClick={handleClose}>
              <i className="fas fa-times"></i>
            </button>
          </div>
        </div>
        {renderContent()}
      </div>
    </div>
  );
};

export default EvidenceModal;
